import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../../common/hooks/useActionDuration';
import { useStageAtoms } from '../../../../../labelling-v2/components/stage-scope/hooks';
import { ANNOTATION_TYPE } from '../../../../constants';
import useRotation from '../../../../providers/hooks/use-rotation';
import useStage from '../../../../providers/hooks/use-stage';
import type { StagePointerEventT } from '../../../../providers/stage-provider';
//interface or types
import type { RectangleAnnotationT } from '../../../../types';
import { isLabelingMode } from '../../../toolbar/utils';
import { getDrawingRectCoords } from '../utils';
interface ParamsT {
  onRectangleCreation: (x: RectangleAnnotationT, params: object) => void;
  initialState?: RectangleAnnotationT | null;
}
const useRectangle = (isDrawing: boolean, {
  onRectangleCreation,
  initialState
}: ParamsT) => {
  const {
    stage
  } = useStage();
  const {
    editorModeAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const [rect, setRect] = useState<RectangleAnnotationT | undefined | null>(initialState);
  const [labelCoords, setLabelCoords] = useState<null | [number, number]>(null);
  const [centerCoords, setCenterCoords] = useState<[number, number]>([0, 0]);
  const [initialPoint, setInitialPoint] = useState([0, 0]);
  const {
    rotationAngle,
    setRotationAngle
  } = useRotation();
  const {
    start,
    end
  } = useActionDuration();
  useEffect(() => {
    if (!isDrawing && rect) {
      const center: [number, number] = [rect.data.x + rect.data.width / 2, rect.data.y + rect.data.height / 2];
      setLabelCoords(center);
      setCenterCoords(center);
    }
  }, [isDrawing, rect]);
  useEffect(() => {
    setRect(initialState);
  }, [initialState]);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;
    if (!annotationLabel) return;
    const initialRect: RectangleAnnotationT = {
      annotationLabel,
      annotationType: ANNOTATION_TYPE.RECTANGLE,
      data: {
        x: mousePos[0],
        y: mousePos[1],
        height: 0,
        width: 0,
        rotationAngle: rotationAngle
      }
    };
    setRect(initialRect);
    setInitialPoint(mousePos);
    start();
  }, [annotationLabel, rotationAngle, start]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    if (!rect) return;
    const {
      mousePos
    } = stageEvent.detail;
    setRect({
      ...rect,
      data: {
        // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
        ...getDrawingRectCoords(initialPoint[0], initialPoint[1], mousePos[0], mousePos[1]),
        rotationAngle: rotationAngle
      }
    });
  }, [rect, initialPoint, rotationAngle]);
  const onPointerUp = useCallback(() => {
    if (rect) {
      if (rect.data.width > 0 && rect.data.height > 0) {
        onRectangleCreation?.(rect, {
          actionDuration: end()
        });
      }
      setRect(null);
    }
  }, [rect, onRectangleCreation, end]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode) || !isDrawing) return;
    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    return () => {
      stage.removeEventListener('pointerdown', onPointerDown);
      stage.removeEventListener('pointermove', onPointerMove);
      stage.removeEventListener('pointerup', onPointerUp);
    };
  }, [stage, rect, onPointerDown, onPointerMove, onPointerUp, editorMode, isDrawing]);
  return {
    rect,
    labelCoords,
    centerCoords,
    setRotationAngle,
    rotationAngle,
    setRect
  };
};
export default useRectangle;